import { FC, useCallback, useMemo } from 'react';
import styles from './style.module.css';
import { HistoryIcon, HospitalIcon, MedicalFormIcon } from '@ubie/ubie-icons';
import { MedicalInstitutionIntroCard } from '../MedicalInstitutionIntroCard/MedicalInstitutionIntroCard';
import { MedicalInstitutionIntroCardSection } from '../MedicalInstitutionIntroCardSection/MedicalInstitutionIntroCardSection';
import { Button } from '@ubie/ubie-ui';
import Link from 'next/link';
import { MedicalInstitutionTopFragmentDoc } from 'gen/medinavi-backend-consumer-graphql/graphql';
import { FragmentType, useFragment } from 'gen/medinavi-backend-consumer-graphql';
import { MedicalInstitutionInformationCard } from '../MedicalInstitutionInformationCard/MedicalInstitutionInformationCard';
import { MedicalInstitutionGuidanceSection } from '../MedicalInstitutionGuidanceSection/MedicalInstitutionGuidanceSection';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import { MessageFromUbieAimon } from '~/components/features/medical-institution/MessageFromUbieAimon';

type Props = {
  keyOrId: string;
  canonicalUrl?: string;
  institution: FragmentType<typeof MedicalInstitutionTopFragmentDoc>;
};

export const MedicalInstitutionTop: FC<Props> = ({ keyOrId, canonicalUrl, institution: institutionFragment }) => {
  const router = useRouter();
  const doneScreenKey = router.query['done_screen_key'] as string | null;
  const toririnrinId = router.query['trd'] as string | null;

  const institution = useFragment(MedicalInstitutionTopFragmentDoc, institutionFragment);

  const startUrl = useMemo(() => {
    const path = `/monshin/${keyOrId}/start`;
    const queryParams = new URLSearchParams();
    if (doneScreenKey == null && toririnrinId == null) {
      queryParams.append('clear_inquiry_settings', 'true');
    } else {
      if (doneScreenKey != null) {
        queryParams.append('done_screen_key', doneScreenKey);
      }
      if (toririnrinId != null) {
        queryParams.append('trd', toririnrinId);
      }
    }
    return new URL(`${path}?${queryParams}`, process.env.NEXT_PUBLIC_INQUIRY_URL).toString();
  }, [keyOrId, doneScreenKey, toririnrinId]);

  const onStart = useCallback(() => {
    router.push(startUrl);
  }, [router, startUrl]);

  const features = useMemo(
    () => [
      {
        icon: HistoryIcon,
        title: 'スムーズな診察が受けられる',
        body: '来院前に問診を済ませておくことで、待合室で改めて問診する必要がありません',
      },
      {
        icon: MedicalFormIcon,
        title: '適切な受診方法の案内が受けられる',
        body: '来院前に問診内容を伝えることで、より適切な受診方法をご案内できます',
      },
    ],
    [],
  );

  return (
    <>
      <NextSeo
        noindex={false}
        title={`${institution.name}のスマートフォン問診`}
        description={`${institution.name}のスマートフォン問診ページです。来院前に問診を済ませておくことで、待合室で改めて問診する必要がなく、スムーズに診察を受けていただくことができます。`}
        canonical={canonicalUrl}
      />
      <div className={styles.root}>
        <div>
          <div className={styles.icon}>
            <HospitalIcon />
          </div>
          <h1 className={styles.title}>{institution.name}</h1>
        </div>
        <section className={styles.introductions}>
          <h2 className={styles.subtitle}>スマートフォン問診</h2>
          <MessageFromUbieAimon style={'alert-top'} />
          {institution.guidance && <MedicalInstitutionGuidanceSection guidance={institution.guidance} />}
          <MedicalInstitutionIntroCard title="スマートフォン問診でできること">
            {features.map((feature) => (
              <MedicalInstitutionIntroCardSection
                key={feature.title}
                title={
                  <div className={styles.cardSectionTitle}>
                    <feature.icon className={styles.cardSectionIcon} />
                    <span>{feature.title}</span>
                  </div>
                }
              >
                <div className={styles.cardSectionBody}>{feature.body}</div>
              </MedicalInstitutionIntroCardSection>
            ))}

            <div className={styles.memo}>
              回答が終わった直後、
              <mark>「受付番号」</mark>
              が表示されます。受診の際に受付で必要です。必ずメモの準備をしてください
            </div>
          </MedicalInstitutionIntroCard>
          <MedicalInstitutionInformationCard institution={institution} />
          <Link className={styles.poweredBy} href="https://ubie.app">
            Powered by ユビー
          </Link>
        </section>
        <div className={styles.startButton}>
          <Button block onClick={onStart}>
            スマートフォン問診をはじめる
          </Button>
        </div>
      </div>
    </>
  );
};
